import React, { useState, useRef } from 'react';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import { Modal, Button } from 'react-bootstrap';
import isURL from 'validator/lib/isURL';
import isEmail from 'validator/lib/isEmail';

function App() {
  // State variables
  const [link, setLink] = useState('');
  const [formatChoice, setFormatChoice] = useState('1');
  const [colorChoice, setColorChoice] = useState('1');
  const [email, setEmail] = useState('');
  const [fileName, setFileName] = useState('qrcode');
  const [showModal, setShowModal] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);

  // Reference to the QR code div for scrolling
  const qrCodeRef = useRef(null);

  // Google Form details
  const GOOGLE_FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSc4vZZ_Z67465ghRkpozjwUR_m6nL2jrxy4fhy0LWaUwmvJvA/formResponse'; 
  const EMAIL_ENTRY_ID = 'entry.1192291706';
  const LINK_ENTRY_ID = 'entry.1914144453'; 

  // Open modal
  const handleShowModal = () => {
    if (link.trim() === '' || !isURL(link)) {
      alert('Please enter a valid URL.');
      return;
    }
    setShowModal(true);
  };

  // Close modal
  const handleCloseModal = () => setShowModal(false);

  // Generate QR Code after email is provided
  const handleGenerateQRCode = () => {
    if (email.trim() === '' || !isEmail(email)) {
      alert('Please enter a valid email address.');
      return;
    }

    // Send data to Google Form
    try {
      // Create a form element programmatically to send data to Google Form
      const form = document.createElement('form');
      form.action = GOOGLE_FORM_URL;
      form.method = 'POST';
      form.target = 'hidden_iframe'; // Set target to a hidden iframe to prevent redirect

      // Create input elements for email and link
      const emailField = document.createElement('input');
      emailField.type = 'hidden';
      emailField.name = EMAIL_ENTRY_ID;
      emailField.value = email;
      form.appendChild(emailField);

      const linkField = document.createElement('input');
      linkField.type = 'hidden';
      linkField.name = LINK_ENTRY_ID;
      linkField.value = link;
      form.appendChild(linkField);

      // Append the form to the document body and submit it
      document.body.appendChild(form);
      form.submit();

      // Remove the form after submission
      document.body.removeChild(form);

      console.log('Data saved successfully to Google Sheets');
    } catch (error) {
      console.error('Error saving data:', error);
    }

    // Show QR code and close modal
    setShowQRCode(true);
    handleCloseModal();

    // Auto scroll to the QR code
    setTimeout(() => {
      if (qrCodeRef.current) {
        qrCodeRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 300);
  };

  // Reset the form to default values
  const resetForm = () => {
    setLink('');
    setFormatChoice('1');
    setColorChoice('1');
    setEmail('');
    setFileName('qrcode');
    setShowQRCode(false);
  };

  // Handle QR code download
  const handleDownload = (e) => {
    e.preventDefault();
    if (formatChoice === '3') {
      // For SVG download
      const svgElement = document.querySelector('svg');
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const linkElement = document.createElement('a');
      linkElement.href = URL.createObjectURL(blob);
      linkElement.download = `${fileName}.svg`;
      linkElement.click();
    } else {
      // For PNG and JPEG download
      const canvas = document.querySelector('canvas');
      const linkElement = document.createElement('a');
      linkElement.href = canvas.toDataURL(formatChoice === '1' ? 'image/png' : 'image/jpeg');
      linkElement.download = `${fileName}.${formatChoice === '1' ? 'png' : 'jpg'}`;
      linkElement.click();
    }
    resetForm(); // Reset form after download
  };

  return (

    <div className="container mt-5">
      <div className="card shadow mx-auto" style={{ maxWidth: '600px' }}>
        <div className="card-header text-white text-center" style={{ backgroundColor: '#004A94' }}>
          <h2>QR Code Generator</h2>
        </div>
        <div className="card-body">
          <div className="form-group mb-3">
            <label>Link to Encode:</label>
            <input
              type="text"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              className="form-control"
              placeholder="Enter the link here"
            />
          </div>

          <div className="form-group mb-3">
            <label>Output Format:</label><br />
            <div className="form-check">
              <input
                type="radio"
                name="formatChoice"
                value="1"
                checked={formatChoice === '1'}
                onChange={() => setFormatChoice('1')}
                className="form-check-input"
              />
              <label className="form-check-label">PNG</label>
            </div>
            <div className="form-check">
              <input
                type="radio"
                name="formatChoice"
                value="2"
                checked={formatChoice === '2'}
                onChange={() => setFormatChoice('2')}
                className="form-check-input"
              />
              <label className="form-check-label">JPEG</label>
            </div>
            <div className="form-check">
              <input
                type="radio"
                name="formatChoice"
                value="3"
                checked={formatChoice === '3'}
                onChange={() => setFormatChoice('3')}
                className="form-check-input"
              />
              <label className="form-check-label">SVG</label>
            </div>
          </div>

          {formatChoice !== '1' && (
            <div className="form-group mb-3">
              <label>Color Option:</label><br />
              <div className="form-check">
                <input
                  type="radio"
                  name="colorChoice"
                  value="1"
                  checked={colorChoice === '1'}
                  onChange={() => setColorChoice('1')}
                  className="form-check-input"
                />
                <label className="form-check-label">White background with black QR code</label>
              </div>
              <div className="form-check">
                <input
                  type="radio"
                  name="colorChoice"
                  value="2"
                  checked={colorChoice === '2'}
                  onChange={() => setColorChoice('2')}
                  className="form-check-input"
                />
                <label className="form-check-label">Black background with white QR code</label>
              </div>
            </div>
          )}

          <div className="form-group mb-3">
            <label>Custom File Name (without extension):</label>
            <input
              type="text"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              className="form-control"
              placeholder="Enter file name for QR code"
            />
          </div>

          <button
            className="btn btn-primary btn-block mt-3"
            onClick={handleShowModal}
            style={{ backgroundColor: '#004A94', borderColor: '#004A94' }}
          >
            Generate & Download QR Code
          </button>

          {showQRCode && (
            <div className="mt-4 text-center" ref={qrCodeRef}>
              {formatChoice === '3' ? (
                <QRCodeSVG
                  value={link}
                  size={1080}
                  includeMargin
                  className="img-fluid"
                  style={{ maxWidth: '250px', height: 'auto' }}
                  bgColor={colorChoice === '1' ? 'white' : 'black'}
                  fgColor={colorChoice === '1' ? 'black' : 'white'}
                />
              ) : (
                <QRCodeCanvas
                  value={link}
                  size={1080}
                  includeMargin
                  className="img-fluid"
                  style={{ maxWidth: '250px', height: 'auto' }}
                  bgColor={formatChoice === '1' ? 'transparent' : colorChoice === '1' ? 'white' : 'black'}
                  fgColor={formatChoice === '1' ? 'black' : colorChoice === '1' ? 'black' : 'white'}
                />
              )}

              <p className="mt-3">
                <button
                  onClick={handleDownload}
                  className="btn btn-link"
                >
                  Download QR Code
                </button>
              </p>
            </div>
          )}

          {/* Modal for Email Entry */}
          <Modal show={showModal} onHide={handleCloseModal}>
            <Modal.Header closeButton>
              <Modal.Title>Enter Your Email</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="form-group">
                <label>Email Address:</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-control"
                  placeholder="Enter your email"
                />
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Close
              </Button>
              <Button variant="primary" onClick={handleGenerateQRCode} style={{ backgroundColor: '#004A94', borderColor: '#004A94' }}>
                Submit & Generate
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Hidden iframe to prevent redirect */}
          <iframe name="hidden_iframe" style={{ display: 'none' }} title="Hidden Frame for Form Submission"></iframe>
        </div>
      </div>
    </div>
  );
} export default App;
